import { notify } from "@iec-id/ui";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SSO_URL } from "@/lib/sso-url";
import { api } from "@/lib/trpc";
import { useThemeStore } from "@/stores/theme-store";

export type SignOutApi = {
	signOut: () => void;
	pending: boolean;
};

/**
 * Выход из аккаунта. После ответа сервера уводим на страницу входа полной
 * навигацией, а не роутером: кэш TanStack Query и стор обязаны умереть вместе
 * с сессией, иначе следующий вошедший увидит чужой баланс в шапке.
 */
export function useSignOut(): SignOutApi {
	const { t } = useTranslation("common");
	const mutation = api.auth.signOut.useMutation({
		onSuccess: (result) => {
			window.location.href = result.redirectTo ?? SSO_URL;
		},
		onError: () => {
			notify.error(t("accountMenu.signOutError"));
		},
	});

	return {
		signOut: () => mutation.mutate({ everywhere: false }),
		pending: mutation.isPending,
	};
}

/**
 * Подписка на системную тему. Стор сам применяет атрибут при выборе и при
 * гидрации, но «по системе» меняется и без участия пользователя — без этой
 * подписки переключение темы в ОС витрина заметит только после перезагрузки.
 */
export function useThemeSync(): void {
	const syncSystem = useThemeStore((state) => state.syncSystem);

	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return;
		const media = window.matchMedia("(prefers-color-scheme: dark)");
		syncSystem();
		media.addEventListener("change", syncSystem);
		return () => media.removeEventListener("change", syncSystem);
	}, [syncSystem]);
}
